/**
 * Two stacked cards near the top of every board:
 *
 *   1. "Your intent" — private free-text the user writes about what
 *      they want out of this board. Editable inline; only ever shown
 *      to its author. Empty state prompts to fill it in.
 *
 *   2. "Shared intent" — AI-synthesised view of the group's intents.
 *      Anonymised; visible to all members. Owners can refresh.
 *
 * Privacy contract:
 *   - body is fetched per-user via /me/intent; never multiplexed.
 *   - the synthesis prompt is explicitly instructed to never name or
 *     directly quote anyone.
 */

import { useEffect, useState } from 'react';
import { api } from '../api';
import { Modal } from './Modal';
import { Avatar } from './Avatar';
import { Spinner } from './Spinner';
import { IconCheck, IconSparkles, IconChevronRight, IconClock } from './Icon';
import { UiNode } from '../lib/UiNode';
import { parseMarkdownLite } from '../../shared/markdown-lite';
import type { BoardDetail } from '../../shared/types';

export function IntentPanel({ board }: { board: BoardDetail }) {
  const [myBody, setMyBody] = useState<string | null>(null);
  const [myLoading, setMyLoading] = useState(true);
  const [editing, setEditing] = useState(false);

  const [status, setStatus] = useState<{
    filled: number;
    total: number;
    pendingInvites: number;
    members: Array<{ userId: string; name: string; filled: boolean; isMe: boolean }>;
  } | null>(null);
  const [summary, setSummary] = useState<{
    summaryMd: string;
    contributorCount: number;
    updatedAt: number;
    stale: boolean;
  } | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [synthBusy, setSynthBusy] = useState(false);
  const [synthError, setSynthError] = useState<string | null>(null);
  const isOwner = board.role === 'owner';

  async function refresh(): Promise<void> {
    // One request: the /intents endpoint now returns both my own
    // intent body AND the group view in a single round-trip (parallel
    // SELECTs server-side). No LLM is called on the read path — the
    // synthesis only runs on the explicit POST /intents/synth.
    const group = await api.intents(board.id).catch(() => null);
    setMyBody(group?.me?.body ?? '');
    setMyLoading(false);
    if (group) {
      setStatus({
        filled: group.contributorCount,
        total: group.memberCount,
        pendingInvites: group.pendingInviteCount,
        members: group.status,
      });
      setSummary(group.summary);
    }
  }

  useEffect(() => {
    void refresh();
    // Re-fetch on broadcast: another member submitted, or the auto-synth
    // just fired now that everyone's in. BoardView's WebSocket handler
    // dispatches this for any intent.updated / intent.synth activity.
    const onChange = () => void refresh();
    window.addEventListener('synfolio:intent-changed', onChange);
    return () => window.removeEventListener('synfolio:intent-changed', onChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board.id]);

  // Auto-open the editor when arriving at /boards/:id#intent (from a
  // nudge in the task inbox). Clear the hash after so refreshes don't
  // reopen indefinitely.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.location.hash === '#intent') {
      setEditing(true);
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, []);

  async function synth() {
    setSynthBusy(true);
    setSynthError(null);
    try {
      const r = await api.synthIntents(board.id);
      setSummary(r.summary);
    } catch (e) {
      setSynthError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setSynthBusy(false);
    }
  }

  const hasMine = !!myBody && myBody.trim().length > 0;
  // "All in" requires both: every joined member submitted AND there
  // are NO pending invites. A synthesis missing one of the invited
  // voices would misrepresent the group, so the Synthesise button
  // stays hidden and auto-synth never fires until everyone's arrived.
  const allIn =
    status != null &&
    status.filled === status.total &&
    status.total > 0 &&
    status.pendingInvites === 0;
  // The panel is expanded while the *current user* still needs to
  // write their intent. Waiting on others' submissions doesn't keep
  // it open — once your bit is done it collapses to a chip showing
  // the overall count, regardless of whether everyone else is in.
  const needsAction = !hasMine;
  const [expanded, setExpanded] = useState(needsAction);
  // Follow needsAction in both directions: collapse the moment the
  // current user submits, re-expand if their intent is cleared.
  useEffect(() => {
    setExpanded(needsAction);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsAction]);

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="panel-elev flex w-full items-center gap-3 p-2.5 text-left text-xs hover:border-cream-3"
        aria-label="Open intent panel"
      >
        <IconSparkles size={12} />
        <span className="text-cream-2">Intent</span>
        <span className="text-cream-3">·</span>
        <span className="text-cream-3">Your intent: written</span>
        {status && (
          <>
            <span className="text-cream-3">·</span>
            <span className="text-cream-3">
              Shared intent: {status.filled}/{status.total} in
              {status.pendingInvites > 0 &&
                ` · ${status.pendingInvites} invite${status.pendingInvites === 1 ? '' : 's'} pending`}
            </span>
          </>
        )}
        <span className="ml-auto text-cream-3">Expand</span>
      </button>
    );
  }

  return (
    <section className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      {!needsAction && (
        <div className="sm:col-span-2 -mb-1 flex justify-end">
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="text-[11px] text-cream-3 hover:text-cream-2"
          >
            Hide
          </button>
        </div>
      )}
      {/* Your intent */}
      <div className="panel p-4">
        <header className="mb-2 flex items-center justify-between gap-2">
          <h3 className="text-sm font-medium">Your intent</h3>
          <div className="flex items-center gap-2">
            {hasMine && (
              <UiNode
                as="button"
                name="intent-history-button"
                entity="intent"
                action="history_open"
                type="button"
                className="inline-flex items-center gap-1 text-[11px] text-cream-3 hover:text-cream-2"
                onClick={() => setHistoryOpen(true)}
                title="See past versions"
              >
                <IconClock size={11} />
                History
              </UiNode>
            )}
            <span className="text-[11px] text-cream-3">Private to you</span>
          </div>
        </header>
        {myLoading ? (
          <div className="flex items-center gap-2 text-xs text-cream-3">
            <Spinner size={12} /> Loading…
          </div>
        ) : hasMine ? (
          <>
            <p className="line-clamp-3 whitespace-pre-wrap text-sm text-cream-2">{myBody}</p>
            <UiNode
              as="button"
              name="intent-edit-button"
              entity="intent"
              action="edit_start"
              type="button"
              className="btn btn-ghost mt-2 inline-flex items-center gap-1 !px-0 text-xs"
              onClick={() => setEditing(true)}
            >
              Edit
              <IconChevronRight size={11} />
            </UiNode>
          </>
        ) : (
          <>
            <p className="text-sm text-cream-3">
              What do you hope to get out of this board? It's private — only used to help align
              the group.
            </p>
            <UiNode
              as="button"
              name="intent-write-button"
              entity="intent"
              action="edit_start"
              type="button"
              className="btn btn-primary mt-3 text-xs"
              onClick={() => setEditing(true)}
            >
              Write your intent
            </UiNode>
          </>
        )}
      </div>

      {/* Shared intent */}
      <div className="panel p-4">
        <header className="mb-2 flex items-center justify-between gap-2">
          <h3 className="inline-flex items-center gap-1.5 text-sm font-medium">
            <IconSparkles size={12} />
            Shared intent
          </h3>
          <div className="flex items-center gap-2">
            {summary?.stale && (
              <span
                className="chip whitespace-nowrap text-[11px]"
                style={{
                  background: 'color-mix(in oklab, var(--color-rust) 18%, transparent)',
                  color: 'var(--color-rust-2, var(--color-rust))',
                  borderColor: 'color-mix(in oklab, var(--color-rust) 32%, transparent)',
                }}
                title="Some intents have changed since this was synthesised."
              >
                Stale
              </span>
            )}
            {status && (
              <span className="text-[11px] text-cream-3">
                {status.filled}/{status.total} in
                {status.pendingInvites > 0 &&
                  ` · ${status.pendingInvites} invite${status.pendingInvites === 1 ? '' : 's'} pending`}
              </span>
            )}
          </div>
        </header>
        {(() => {
          // "All in" requires both: every joined member submitted AND there
  // are NO pending invites. A synthesis missing one of the invited
  // voices would misrepresent the group, so the Synthesise button
  // stays hidden and auto-synth never fires until everyone's arrived.
  const allIn =
    status != null &&
    status.filled === status.total &&
    status.total > 0 &&
    status.pendingInvites === 0;
          if (summary && allIn) {
            return (
              <div className="whitespace-pre-wrap break-words text-sm text-cream-2">
                <MarkdownLite text={summary.summaryMd} />
              </div>
            );
          }
          if (!allIn && status) {
            const missing = status.members.filter((m) => !m.filled).map((m) => m.name);
            const waiting =
              missing.length === 0
                ? 'Waiting on the rest of the team.'
                : missing.length === 1
                  ? `Waiting on ${missing[0]}.`
                  : missing.length <= 3
                    ? `Waiting on ${missing.slice(0, -1).join(', ')} and ${missing.slice(-1)}.`
                    : `Waiting on ${missing.length} more.`;
            return (
              <p className="text-sm text-cream-3">
                {waiting} Everyone's intent is needed before the AI drafts a shared view.
              </p>
            );
          }
          if (summary && !allIn) {
            // Summary exists but team membership has changed since.
            // Show it stale-flagged but keep visible.
            return (
              <div className="whitespace-pre-wrap break-words text-sm text-cream-2">
                <MarkdownLite text={summary.summaryMd} />
              </div>
            );
          }
          return <p className="text-sm text-cream-3">No synthesis yet.</p>;
        })()}
        {status && status.members.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-ink-rim/60 pt-2.5">
            {status.members.map((m) => (
              <span
                key={m.userId}
                title={`${m.name}${m.filled ? ' · in' : ' · not yet'}`}
                className="inline-flex items-center gap-1 rounded-full px-1 py-0.5 text-[11px]"
                style={{
                  background: m.filled ? 'color-mix(in oklab, var(--color-leaf) 18%, transparent)' : 'transparent',
                  color: m.filled ? 'var(--color-cream)' : 'var(--color-cream-4)',
                }}
              >
                <Avatar
                  user={{ id: m.userId, name: m.name, avatarHue: hueFor(m.userId), avatarUrl: null }}
                  size={16}
                />
                {m.filled && <IconCheck size={10} />}
              </span>
            ))}
          </div>
        )}
        {isOwner && allIn && (
          // The synthesise button is HIDDEN (not just disabled) until
          // every member has shared their intent. The previous gated-
          // disabled look invited clicks and read as 'broken' — better
          // to show it only when it can actually be used.
          <div className="mt-2 flex items-center justify-end gap-2">
            {synthError && <span className="text-[11px] text-rust">{synthError}</span>}
            <UiNode
              as="button"
              name="intent-synthesise-button"
              entity="intent_summary"
              action="regenerate"
              state={synthBusy ? 'busy' : 'idle'}
              type="button"
              className="btn btn-ghost inline-flex items-center gap-1.5 text-xs"
              onClick={synth}
              disabled={synthBusy}
              title="Regenerate shared intent"
            >
              {synthBusy ? <Spinner size={10} /> : <IconSparkles size={10} />}
              {summary ? 'Regenerate' : 'Synthesise'}
            </UiNode>
          </div>
        )}
      </div>

      {editing && (
        <IntentEditor
          board={board}
          initial={myBody ?? ''}
          onClose={() => setEditing(false)}
          onSaved={(b) => {
            setMyBody(b);
            setEditing(false);
            void refresh();
            // Tell any tasks UI in the page that nudges are stale —
            // saves a reload to clear the "Write your intent" nudge.
            window.dispatchEvent(new CustomEvent('synfolio:tasks-stale'));
          }}
        />
      )}
      {historyOpen && (
        <IntentHistory boardId={board.id} onClose={() => setHistoryOpen(false)} />
      )}
    </section>
  );
}

function IntentHistory({
  boardId,
  onClose,
}: {
  boardId: string;
  onClose: () => void;
}) {
  const [versions, setVersions] = useState<
    Array<{ id: string; body: string; createdAt: number }> | null
  >(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .myIntentHistory(boardId)
      .then((r) => {
        if (!cancelled) setVersions(r.versions);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed'));
    return () => {
      cancelled = true;
    };
  }, [boardId]);

  return (
    <Modal open onClose={onClose} title="Your intent · history" maxWidth={520}>
      <div className="flex max-h-[70vh] flex-col gap-3 overflow-y-auto">
        <p className="text-xs text-cream-3">
          Every edit you've saved. Private to you. Latest first.
        </p>
        {error && <p className="text-xs text-rust">{error}</p>}
        {versions === null ? (
          <div className="flex items-center gap-2 text-xs text-cream-3">
            <Spinner size={12} />
            Loading…
          </div>
        ) : versions.length === 0 ? (
          <p className="text-sm text-cream-3">No history yet.</p>
        ) : (
          <ul className="flex flex-col gap-3">
            {versions.map((v, i) => (
              <li key={v.id} className="panel-elev p-3">
                <div className="mb-1 flex items-center gap-2 text-[11px] text-cream-3">
                  <span>{relativeTime(v.createdAt)}</span>
                  {i === 0 && <span className="chip text-[10px]">current</span>}
                </div>
                <p className="whitespace-pre-wrap break-words text-sm">{v.body}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}

function relativeTime(unixSec: number): string {
  const d = Math.floor(Date.now() / 1000) - unixSec;
  if (d < 60) return 'just now';
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  if (d < 604800) return `${Math.floor(d / 86400)}d ago`;
  return new Date(unixSec * 1000).toLocaleDateString();
}

function IntentEditor({
  board,
  initial,
  onClose,
  onSaved,
}: {
  board: BoardDetail;
  initial: string;
  onClose: () => void;
  onSaved: (body: string) => void;
}) {
  const [body, setBody] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <Modal open onClose={onClose} title="Your intent" maxWidth={520}>
      <form
        className="flex flex-col gap-3"
        onSubmit={async (e) => {
          e.preventDefault();
          const trimmed = body.trim();
          if (!trimmed || busy) return;
          setBusy(true);
          try {
            await api.saveMyIntent(board.id, trimmed);
            onSaved(trimmed);
          } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed');
          } finally {
            setBusy(false);
          }
        }}
      >
        <p className="text-xs text-cream-3">
          Only you can read this. The AI uses it (along with everyone else's) to draft an
          anonymised shared view — your words are never quoted directly or attributed.
        </p>
        <textarea
          className="input min-h-[140px] resize-y text-sm"
          autoFocus
          placeholder={'e.g. "I want us to ship something tangible by Friday, and I want to leave with a clearer view of how AI fits into the product."'}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        {error && <p className="text-xs text-rust">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <UiNode
            as="button"
            name="intent-save-button"
            entity="intent"
            action="save"
            state={busy ? 'busy' : 'idle'}
            type="submit"
            className="btn btn-primary"
            disabled={busy || !body.trim()}
          >
            {busy ? 'Saving…' : 'Save'}
          </UiNode>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Thin React shell over the shared parseMarkdownLite. Keeps the
 * intent-synthesis renderer trivial — heading / paragraph / bullets,
 * plus inline bold. Anything fancier and the synthesis prompt is
 * lying about its output.
 */
function MarkdownLite({ text }: { text: string }) {
  const blocks = parseMarkdownLite(text);
  return (
    <>
      {blocks.map((b, i) => {
        if (b.type === 'heading') {
          return (
            <p key={`h-${i}`} className="mt-2 text-sm font-medium text-cream">
              <Inline nodes={b.text} />
            </p>
          );
        }
        if (b.type === 'list') {
          return (
            <ul key={`ul-${i}`} className="ml-5 list-disc space-y-1">
              {b.items.map((it, j) => (
                <li key={`li-${i}-${j}`}>
                  <Inline nodes={it} />
                </li>
              ))}
            </ul>
          );
        }
        return (
          <p key={`p-${i}`} className="text-sm">
            <Inline nodes={b.text} />
          </p>
        );
      })}
    </>
  );
}

function Inline({ nodes }: { nodes: import('../../shared/markdown-lite').MdInline[] }) {
  return (
    <>
      {nodes.map((n, i) =>
        n.type === 'bold' ? <strong key={i}>{n.text}</strong> : <span key={i}>{n.text}</span>,
      )}
    </>
  );
}

function hueFor(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return h % 360;
}
